/**
 * The legacy opening crow tour (`createScene.ts` `startCrowTour` … `skipCrowTourCamera`)
 * on native routes. The bird rides the chase route's progress, read after the route
 * tick and before the render (`camera:update`), so the two cannot drift apart however
 * the route's clock runs (C14); a hand-back route returns the camera afterwards.
 */
import type { DatumEngine } from '@datum-sdk/engine';
import type { CrowTourConfig } from '../../sceneConfig';
import type { Vector3Like } from '../../props/crowFlight';
import type { CrowTourParams } from '../../props/crowTour';
import type { CrowBirdPoi } from './crowBirdPoi';
import type { SdkCrowBird } from './sdkCrowBird';
import {
  CHASE_ROUTE_ID,
  HANDBACK_ROUTE_ID,
  buildChaseRoute,
  buildHandBackRoute,
  tourFromPerch,
  type ChasePose,
} from './crowChaseRoute';

/** Legacy `CROW_TOUR_SKIP_HANDBACK_SECONDS`: a skip is a quick way out, not the closing shot. */
const SKIP_HANDBACK_SECONDS = 1.2;

export interface CrowOpeningTour {
  /** Flies the tour; `onDone` once the camera is handed back — at once when there is none. */
  play(onDone: () => void): void;
  /** Dev: the tour again, from where the camera is, with the settings as they are now. */
  replay(): void;
  /** Dev: the chase held at `progress` — a tap leaves it, as a skip. Null with nothing to fly. */
  scrub(progress: number): { progress: number; fov: number } | null;
  /** From take-off until the chase ends, is skipped or is cut short. */
  isChasing(): boolean;
  dispose(): void;
}

interface ChaseRun {
  params: CrowTourParams;
  entry: ChasePose;
  /** Route progress the bird was last posed at — tour progress. */
  progress: number;
  ended: boolean;
}

export const createCrowOpeningTour = (
  engine: DatumEngine,
  perch: Vector3Like,
  settings: CrowTourConfig,
  bird: SdkCrowBird,
  birdPoi: CrowBirdPoi,
): CrowOpeningTour => {
  let chase: ChaseRun | null = null;
  let onDone = (): void => {};
  let disposed = false;
  // Capture on the engine container: the cinematic's input blocker sits inside
  // it, and with no blocker the tap lands on the canvas, inside it too.
  const skipTarget = engine.getContainer();

  const onCameraUpdate = (): void => {
    if (!chase || engine.getActiveCameraRouteId() !== CHASE_ROUTE_ID) return;
    const progress = engine.getCameraRouteProgress();
    if (progress === null) return;
    chase.progress = progress;
    bird.poseOnTour(chase.params, progress);
  };

  // After the engine's own hand-back of the route that just ended or stopped.
  const handBack = (entry: ChasePose, durationMs: number): void => {
    queueMicrotask(() => {
      if (disposed) return;
      engine.putCameraRoute(buildHandBackRoute(entry, durationMs));
      engine.startCameraRoute(HANDBACK_ROUTE_ID);
    });
  };
  const finish = (): void => {
    queueMicrotask(() => {
      // A replay started over the hand-back owns the host now.
      if (disposed || (chase && !chase.ended)) return;
      engine.setRuntimeOptions({ controls: { enabled: true } });
      onDone();
    });
  };

  // The chase is over — run out, skipped or cut short — and its listeners go with it.
  const endChase = (run: ChaseRun): void => {
    run.ended = true;
    engine.off('camera:update', onCameraUpdate);
    skipTarget.removeEventListener('pointerdown', skip, true);
    skipTarget.removeEventListener('wheel', skip, true);
  };
  // The bird flies the rest of the tour alone, its POI withheld until it lands.
  const flyBirdAlone = (run: ChaseRun): void => {
    birdPoi.withhold();
    bird.flyRestOfTour(run.params, run.progress, birdPoi.restore);
  };

  // Legacy `skipCrowTourCamera`: a tap or wheel goes to where the route would
  // have left the camera, quickly, and the bird flies the rest of it alone.
  const skip = (): void => {
    if (!chase || chase.ended) return;
    endChase(chase);
    flyBirdAlone(chase);
    engine.stopCameraRoute();
    handBack(chase.entry, SKIP_HANDBACK_SECONDS * 1000);
  };

  const onRouteEnd = ({ routeId }: { routeId: string }): void => {
    if (!chase) return;
    if (routeId === CHASE_ROUTE_ID) {
      endChase(chase);
      bird.land(chase.params);
      handBack(chase.entry, settings.handBackSeconds * 1000);
    } else if (routeId === HANDBACK_ROUTE_ID) {
      finish();
    }
  };
  // Cut short from outside — a camera flight started over it, such as a chat
  // `focus-sight` landing mid-tour: the camera is that flight's now, the bird flies
  // on alone as after a skip, and the host is released. (A skip ended the chase first.)
  const onRouteStop = ({ routeId }: { routeId: string }): void => {
    if (routeId === CHASE_ROUTE_ID && chase && !chase.ended) {
      endChase(chase);
      flyBirdAlone(chase);
      finish();
    } else if (routeId === HANDBACK_ROUTE_ID) {
      finish();
    }
  };

  /** False when the route is too short to fly. */
  const start = (): boolean => {
    const params = tourFromPerch(settings, perch);
    if (!params) return false;
    const state = engine.getCameraState();
    const entry: ChasePose = {
      position: [state.position[0], state.position[1], state.position[2]],
      lookAt: [state.lookAt[0], state.lookAt[1], state.lookAt[2]],
      // A replay keeps the first entry's fov: the live one may still be a close-up's.
      fov: chase?.entry.fov ?? state.fov,
    };
    if (!chase) {
      engine.on('route:end', onRouteEnd);
      engine.on('route:stop', onRouteStop);
    }
    chase = { params, entry, progress: 0, ended: false };
    // Sole writer of the camera pose for the duration, as in legacy.
    engine.setRuntimeOptions({ controls: { enabled: false } });
    engine.putCameraRoute(buildChaseRoute(params, settings, entry));
    engine.on('camera:update', onCameraUpdate);
    skipTarget.addEventListener('pointerdown', skip, true);
    skipTarget.addEventListener('wheel', skip, true);
    bird.startWings();
    engine.startCameraRoute(CHASE_ROUTE_ID);
    return true;
  };

  const replay = (): void => {
    if (disposed) return;
    // Ended first, so stopping its route reads as neither a skip nor a flight cutting in.
    if (chase && !chase.ended) endChase(chase);
    const active = engine.getActiveCameraRouteId();
    if (active === CHASE_ROUTE_ID || active === HANDBACK_ROUTE_ID) engine.stopCameraRoute();
    bird.stopFlying();
    birdPoi.restore();
    if (!start()) finish();
  };

  return {
    play: (done) => {
      onDone = done;
      if (!start()) done();
    },
    replay,
    scrub: (progress) => {
      if (disposed) return null;
      if (!chase || chase.ended) replay();
      if (!chase || chase.ended) return null;
      const at = Math.min(1, Math.max(0, progress));
      engine.setCameraRouteProgress(at);
      chase.progress = at;
      bird.poseOnTour(chase.params, at);
      return { progress: at, fov: engine.getCameraState().fov };
    },
    isChasing: () => chase !== null && !chase.ended,
    dispose: () => {
      disposed = true;
      if (chase && !chase.ended) endChase(chase);
      engine.off('route:end', onRouteEnd);
      engine.off('route:stop', onRouteStop);
      chase = null;
    },
  };
};
